import React, { useEffect, useState } from "react";
import { jsPDF } from "jspdf";
import Navbar from './Navbar';
import '../Css/ManagementPage.css';
import { Card, CardContent, Typography, Button } from "@mui/material";
import QRCode from "qrcode";
import Logger from "./Logger";
import { getLoggerBaseURL } from '../utils/getLoggerBaseURL';
import PaginationControls from "./Pagination";

const QueueDataList = () => {
  const [queueData, setQueueData] = useState([]);
  const [totalCount, setTotalCount] = useState([]);
  const [selectedDate, setSelectedDate] = useState(""); // Added for date filter
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 50;
  const summaryOrdersLength = sessionStorage.getItem('summaryOrdersLength');

  // Initialize Logger
  const logger = new Logger(getLoggerBaseURL());

  useEffect(() => {
    // whenever date filter changes, always reset to page 1
    setCurrentPage(1);
  }, [selectedDate]);

  useEffect(() => {
    const fetchQueueData = async () => {
      const adminEmail = sessionStorage.getItem("adminEmail");
      if (!adminEmail) return;

      const params = new URLSearchParams({
        page: currentPage,
        limit: itemsPerPage,
      });

      if (selectedDate) {
        params.append("date", `${selectedDate}T00:00:00`);
      }

      const response = await fetch(`/api/DrOrder/all/getQueue/${adminEmail}?${params.toString()}`);

      const result = await response.json();
      setQueueData(result.data);
      setTotalCount(result.pagination.total);
    };

    fetchQueueData();
  }, [currentPage, selectedDate]);

  const handleGeneratePDF = async (item) => {
    const { Order_No, Summary_No, noOfCopies, noOfPackets, noOfBoxes, weightOfOrder, area } = item.data;
    const { key } = item;
    const noOfOrders = item.data.noOfOrders ?? null;

    if (!key) {
      logger.log('Missing key value for PDF generation', 'ERROR');
      return;
    }

    try {
      const qrCodeDataURL = await QRCode.toDataURL(key);
      const doc = new jsPDF({ unit: "pt", format: "a4", orientation: "landscape" });

      const contentWidth = 216;
      const topMargin = 20;

      for (let i = 0; i < noOfCopies; i++) {
        if (i > 0) doc.addPage();

        doc.setFont("helvetica", "bold");
        doc.setFontSize(12);
        doc.text("Order Details", contentWidth / 2, topMargin + 10, { align: "center" });

        doc.setFontSize(10);
        doc.setFont("helvetica", "normal");

        const textStartX = contentWidth - 130;
        const qrCodeX = 10;
        const qrCodeSize = 70;
        const qrCodeY = topMargin + 20;
        let textStartY = topMargin + 35;
        let lineSpacing = 10;

        doc.addImage(qrCodeDataURL, "PNG", qrCodeX, qrCodeY, qrCodeSize, qrCodeSize);

        if (Summary_No) {
          doc.text(`Summary: ${Summary_No}`, textStartX, textStartY);
          textStartY += lineSpacing;
          doc.text(`No. of Orders: ${noOfOrders || summaryOrdersLength}`, textStartX, textStartY);
        } else {
          doc.text(`Order: ${Order_No}`, textStartX, textStartY);
        }
        textStartY += lineSpacing;
        doc.text(`Packets: ${noOfPackets}`, textStartX, textStartY);
        textStartY += lineSpacing;
        doc.text(`Boxes: ${noOfBoxes}`, textStartX, textStartY);
        textStartY += lineSpacing;
        doc.text(`Weight: ${weightOfOrder} kg`, textStartX, textStartY);
        textStartY += lineSpacing;
        doc.text(`Area: ${area}`, textStartX, textStartY);
      }

      doc.autoPrint();
      window.open(doc.output("bloburl"), "_blank");

    } catch (error) {
      logger.log(`Error generating QR code or PDF: ${error.message}`, 'ERROR');
    }
  };

  return (
    <div className="mgmt-page">
      <Navbar />

      <div className="mgmt-page-header">
        <div>
          <h2 className='headS'>Print Requests</h2>
        </div>
        <div className="mgmt-actions">
          <div className="mgmt-filter">
            <label htmlFor="dateFilter">Filter by date</label>
            <input
              type="date"
              id="dateFilter"
              value={selectedDate}
              onChange={(e) => {
                setSelectedDate(e.target.value);
                setCurrentPage(1);
              }}
              className="mgmt-date-input"
            />
          </div>
          <div className="mgmt-count-plain">
            Total Count : {totalCount}
          </div>
        </div>
      </div>

      <div className="mgmt-card">
        <div className="print-request-grid">
          {queueData.length > 0 ? (
            queueData.map((item) => (
              <Card
                key={item._id}
                sx={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  boxShadow: "none",
                  borderRadius: "12px",
                  border: "1px solid #e2e8f0",
                  transition: "transform 0.2s ease, box-shadow 0.2s ease",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: "0 12px 24px rgba(15, 23, 42, 0.08)",
                  },
                }}
              >
                <CardContent>
                  <Typography variant="subtitle1" gutterBottom>
                    {item.data.Summary_No ? "Summary No" : "Order No"} :{" "}
                    <strong>
                      {item.data.Summary_No || item.data.Order_No}
                    </strong>
                  </Typography>

                  <Typography variant="body2" color="text.secondary">
                    <strong>No. of Copies:</strong> {item.data.noOfCopies}
                  </Typography>

                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 0.5 }}
                  >
                    <strong>Date:</strong> {item.timestamp}
                  </Typography>
                </CardContent>

                <div style={{ padding: "16px" }}>
                  {item.processed ? (
                    <Typography
                      color="success.main"
                      fontWeight="bold"
                      textAlign="center"
                    >
                      PDF Downloaded
                    </Typography>
                  ) : (
                    <Button
                      fullWidth
                      variant="contained"
                      sx={{
                        backgroundColor: "#0ea5e9",
                        boxShadow: "none",
                        textTransform: "none",
                        borderRadius: "8px",
                        "&:hover": { backgroundColor: "#0284c7", boxShadow: "none" },
                      }}
                      onClick={() => handleGeneratePDF(item)}
                    >
                      Generate PDF
                    </Button>
                  )}
                </div>
              </Card>
            ))
          ) : (
            <div className="mgmt-empty">
              No queue data found {selectedDate ? "for the selected date" : ""}.
            </div>
          )}
        </div>

      </div>
      <div className="mgmt-pagination">
        <PaginationControls
          key={selectedDate || "no-date"}
          totalItems={totalCount}
          itemsPerPage={itemsPerPage}
          currentPage={currentPage}
          onPageChange={setCurrentPage}
        />
      </div>
    </div>
  );
};

export default QueueDataList;
